import React, {
    useEffect,
    useState
} from "react";

import {
    Link,
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";

import QuillEditor from "../components/QuillEditor";


function EditPaperQuestion() {

    const {
        id
    } = useParams();


    const navigate =
        useNavigate();


    const API_URL =
        `${API_BASE_URL}/admin/paper-questions/${id}`;


    const SUBJECT_API =
        `${API_BASE_URL}/admin/paper-question-subjects`;


    const PAPER_API =
        `${API_BASE_URL}/admin/paper-question-papers`;


    const [subjects, setSubjects] =
        useState([]);


    const [papers, setPapers] =
        useState([]);


    const [loading, setLoading] =
        useState(true);


    const [saving, setSaving] =
        useState(false);


    const [form, setForm] = useState({

        subject: "",
        paper: "",
        question: "",
        sort_order: "",
        option1: "",
        option2: "",
        option3: "",
        option4: "",
        right_option: "",
        explanation: ""

    });


    /*
    |--------------------------------------------------------------------------
    | Load Question + Subjects + Papers
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const loadData = async () => {

            try {

                setLoading(true);


                const [
                    questionResponse,
                    subjectResponse,
                    paperResponse
                ] = await Promise.all([

                    fetch(
                        API_URL,
                        {
                            credentials: "include",
                            headers: {
                                Accept:
                                    "application/json"
                            }
                        }
                    ),

                    fetch(
                        SUBJECT_API,
                        {
                            credentials: "include",
                            headers: {
                                Accept:
                                    "application/json"
                            }
                        }
                    ),

                    fetch(
                        PAPER_API,
                        {
                            credentials: "include",
                            headers: {
                                Accept:
                                    "application/json"
                            }
                        }
                    )

                ]);


                const questionData =
                    await questionResponse.json();


                const subjectData =
                    await subjectResponse.json();


                const paperData =
                    await paperResponse.json();


                if (
                    !questionResponse.ok ||
                    !questionData.success
                ) {

                    throw new Error(
                        questionData.message ||
                        "Unable to load question."
                    );

                }


                const item =
                    questionData.data;


                setForm({

                    subject:
                        item.subject || "",

                    paper:
                        item.paper || "",

                    question:
                        item.question || "",

                    sort_order:
                        item.sort_order ?? "",

                    option1:
                        item.option1 || "",

                    option2:
                        item.option2 || "",

                    option3:
                        item.option3 || "",

                    option4:
                        item.option4 || "",

                    right_option:
                        String(
                            item.right_option || ""
                        ),

                    explanation:
                        item.explanation || ""

                });


                if (subjectData.success) {

                    setSubjects(
                        subjectData.data || []
                    );

                }


                if (paperData.success) {

                    setPapers(
                        paperData.data || []
                    );

                }

            } catch (error) {

                console.error(
                    "Edit Question Load Error:",
                    error
                );


                Swal.fire(
                    "Error",
                    error.message ||
                    "Unable to load question.",
                    "error"
                );

            } finally {

                setLoading(false);

            }

        };


        if (id) {

            loadData();

        }

    }, [id]);


    /*
    |--------------------------------------------------------------------------
    | Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setForm(prev => ({
            ...prev,
            [name]: value
        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Question
    |--------------------------------------------------------------------------
    */

    const handleQuestionChange = (value) => {

        setForm(prev => ({
            ...prev,
            question: value
        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Explanation
    |--------------------------------------------------------------------------
    */

    const handleExplanationChange = (value) => {

        setForm(prev => ({
            ...prev,
            explanation: value
        }));

    };


    /*
    |--------------------------------------------------------------------------
    | Submit Update
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        if (!form.subject) {

            Swal.fire(
                "Required",
                "Please select subject.",
                "warning"
            );

            return;
        }


        if (!form.paper.trim()) {

            Swal.fire(
                "Required",
                "Please enter paper.",
                "warning"
            );

            return;
        }


        if (!form.question.trim()) {

            Swal.fire(
                "Required",
                "Please enter question.",
                "warning"
            );

            return;
        }


        if (!form.sort_order) {

            Swal.fire(
                "Required",
                "Please enter sort order.",
                "warning"
            );

            return;
        }


        if (
            !form.option1.trim() ||
            !form.option2.trim() ||
            !form.option3.trim() ||
            !form.option4.trim()
        ) {

            Swal.fire(
                "Required",
                "Please enter all four options.",
                "warning"
            );

            return;
        }


        if (!form.right_option) {

            Swal.fire(
                "Required",
                "Please select right option.",
                "warning"
            );

            return;
        }


        if (!form.explanation.trim()) {

            Swal.fire(
                "Required",
                "Please enter explanation.",
                "warning"
            );

            return;
        }


        try {

            setSaving(true);


            const response =
                await fetch(
                    API_URL,
                    {
                        method: "PUT",

                        headers: {
                            "Content-Type":
                                "application/json",

                            Accept:
                                "application/json"
                        },

                        credentials: "include",

                        body: JSON.stringify(form)
                    }
                );


            const data =
                await response.json();


            if (response.status === 422) {

                let message =
                    data.message ||
                    "Validation failed.";


                if (data.errors) {

                    const errors =
                        Object.values(
                            data.errors
                        ).flat();


                    message =
                        errors.join("<br>");
                }


                Swal.fire({
                    icon: "error",
                    title: "Validation Failed",
                    html: message
                });


                return;
            }


            if (!response.ok ||
                !data.success
            ) {

                throw new Error(
                    data.message ||
                    "Unable to update question."
                );

            }


            await Swal.fire({
                icon: "success",
                title: "Updated",
                text: "Question updated successfully.",
                confirmButtonText: "OK"
            });


            navigate("/paperquestions");


        } catch (error) {

            console.error(
                "Update Question Error:",
                error
            );


            Swal.fire(
                "Error",
                error.message ||
                "Unable to update question.",
                "error"
            );

        } finally {

            setSaving(false);

        }

    };


    if (loading) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight: "400px"
                }}
            >

                <div className="text-center">

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                    ></div>

                    <div className="mt-3 text-muted">
                        Loading question...
                    </div>

                </div>

            </div>

        );

    }


    return (

        <div
            className="container-fluid"
            style={{
                maxHeight: "85vh",
                overflowY: "auto"
            }}
        >


            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3 className="mb-1">

                        <i className="fa fa-pencil me-2"></i>

                        Edit Question

                    </h3>

                    <p className="text-muted mb-0">
                        Update study material question
                    </p>

                </div>


                <Link
                    to="/paperquestions"
                    className="
                        btn
                        btn-outline-secondary
                    "
                >

                    <i className="fa fa-arrow-left me-2"></i>

                    Back

                </Link>

            </div>


            <div
                className="
                    card
                    shadow-sm
                "
                style={{
                    borderTop:
                        "2px solid #bfbfbf"
                }}
            >


                <div
                    className="
                        card-header
                        bg-white
                    "
                >

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >

                        <h5 className="mb-0">

                            <i className="fa fa-pencil me-2"></i>

                            Edit Question

                        </h5>


                        <span
                            className="
                                badge
                                bg-light
                                text-dark
                                border
                            "
                        >

                            Study ID: {id}

                        </span>

                    </div>

                </div>


                <form
                    onSubmit={handleSubmit}
                >

                    <div className="card-body">


                        {/* Subject */}

                        <div className="row mb-3">

                            <label
                                className="
                                    col-md-2
                                    col-form-label
                                    fw-semibold
                                "
                            >
                                Subject
                            </label>


                            <div className="col-md-10">

                                <select
                                    name="subject"
                                    className="form-select"
                                    value={form.subject}
                                    onChange={handleChange}
                                >

                                    <option value="">
                                        -----Select Subject-----
                                    </option>


                                    {subjects.map(
                                        (item) => (

                                            <option
                                                key={
                                                    item.id
                                                }
                                                value={
                                                    item.subject_name
                                                }
                                            >

                                                {
                                                    item.subject_name
                                                }

                                            </option>

                                        )
                                    )}

                                </select>

                            </div>

                        </div>


                        {/* Paper */}

                        <div className="row mb-3">

                            <label
                                className="
                                    col-md-2
                                    col-form-label
                                    fw-semibold
                                "
                            >
                                Paper
                            </label>


                            <div className="col-md-10">

                                <input
                                    type="text"
                                    name="paper"
                                    list="papers"
                                    className="form-control"
                                    placeholder="
                                        Ex. Paper 1 or Sample Paper
                                    "
                                    value={form.paper}
                                    onChange={handleChange}
                                />


                                <datalist id="papers">

                                    {papers.map(
                                        (item, index) => (

                                            <option
                                                key={index}
                                                value={
                                                    item.paper
                                                }
                                            />

                                        )
                                    )}

                                </datalist>

                            </div>

                        </div>


                        {/* Question */}

                        <div className="row mb-3">

                            <label
                                className="
                                    col-md-2
                                    col-form-label
                                    fw-semibold
                                "
                            >
                                Question
                            </label>


                            <div className="col-md-10">

                                <QuillEditor
                                    value={
                                        form.question
                                    }
                                    onChange={
                                        handleQuestionChange
                                    }
                                />

                            </div>

                        </div>


                        {/* Sort Order */}

                        <div className="row mb-3">

                            <label
                                className="
                                    col-md-2
                                    col-form-label
                                    fw-semibold
                                "
                            >
                                Sort Order
                            </label>


                            <div className="col-md-10">

                                <input
                                    type="number"
                                    name="sort_order"
                                    className="form-control"
                                    value={
                                        form.sort_order
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>

                        </div>


                        {/* Options */}

                        {[
                            "option1",
                            "option2",
                            "option3",
                            "option4"
                        ].map(
                            (option, index) => (

                                <div
                                    className="
                                        row
                                        mb-3
                                    "
                                    key={option}
                                >

                                    <label
                                        className="
                                            col-md-2
                                            col-form-label
                                            fw-semibold
                                        "
                                    >

                                        Option {index + 1}

                                    </label>


                                    <div className="col-md-10">

                                        <input
                                            type="text"
                                            name={option}
                                            className="form-control"
                                            placeholder={
                                                `Option ${index + 1}`
                                            }
                                            value={
                                                form[option]
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </div>

                                </div>

                            )
                        )}


                        {/* Right Option */}

                        <div className="row mb-3">

                            <label
                                className="
                                    col-md-2
                                    col-form-label
                                    fw-semibold
                                "
                            >
                                Right Option
                            </label>


                            <div className="col-md-10">

                                <select
                                    name="right_option"
                                    className="form-select"
                                    value={
                                        form.right_option
                                    }
                                    onChange={
                                        handleChange
                                    }
                                >

                                    <option value="">
                                        -----Select Right Option-----
                                    </option>

                                    <option value="1">
                                        Option 1
                                    </option>

                                    <option value="2">
                                        Option 2
                                    </option>

                                    <option value="3">
                                        Option 3
                                    </option>

                                    <option value="4">
                                        Option 4
                                    </option>

                                </select>

                            </div>

                        </div>


                        {/* Explanation */}

                        <div className="row mb-3">

                            <label
                                className="
                                    col-md-2
                                    col-form-label
                                    fw-semibold
                                "
                            >
                                Explanation
                            </label>


                            <div className="col-md-10">

                                <QuillEditor
                                    value={
                                        form.explanation
                                    }
                                    onChange={
                                        handleExplanationChange
                                    }
                                />

                            </div>

                        </div>

                    </div>


                    <div
                        className="
                            card-footer
                            bg-white
                            text-end
                        "
                    >

                        <Link
                            to="/paperquestions"
                            className="
                                btn
                                btn-secondary
                                me-2
                            "
                        >
                            Cancel
                        </Link>


                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                        >

                            {saving ? (

                                <>
                                    <span
                                        className="
                                            spinner-border
                                            spinner-border-sm
                                            me-2
                                        "
                                    ></span>

                                    Updating...

                                </>

                            ) : (

                                <>
                                    <i className="fa fa-save me-2"></i>

                                    Update Question

                                </>

                            )}

                        </button>

                    </div>

                </form>

            </div>

        </div>

    );

}


export default EditPaperQuestion;